import { Blac, Cubit } from "blac-next";
import { ApiError } from "@9amhealth/openapi";
import reportErrorSentry from "src/lib/reportErrorSentry";
import LoadingCubit, { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import { featureFlags } from "src/lib/featureFlags";
import { globalEvents } from "src/constants/globalEvents";
import { StorageController } from "../StorageBloc/StorageBloc";
import {
  type RefillCohort,
  type RefillCardVariant,
  type RefillRequirementsProgress,
  type RefillRequirementsResponse
} from "./RefillRequirementsTypes";
import { getRefillRequirements } from "./refillRequirementsApi";
import {
  getCountdownFraction,
  getDaysUntilNextRefill,
  getRefillCardVariant,
  getRequirementsProgress,
  parseRefillRequirementsResponse
} from "./RefillRequirementsHelpers";

export interface RefillRequirementsState {
  data?: RefillRequirementsResponse;
  loading: boolean;
  error: string | null;
}

/**
 * Owns refill-requirements state for the hub (FEWEB-2503), the home refill card
 * (FEWEB-2502) and the medication-card entry (FEWEB-2501). keepAlive, cache-first read
 * then background sync, gated by the `refillDecoupling` flag.
 *
 * Fed by `GET /v1/medication-renewal/GLP1_WEIGHTLOSS` via `getRefillRequirements`, which
 * adapts the generated `MedicationRenewalControllerService` response to the FE shape.
 */
export default class RefillRequirementsCubit extends Cubit<RefillRequirementsState> {
  static keepAlive = true;
  private static readonly CACHE_KEY = "refillRequirements";

  constructor() {
    super({ data: undefined, loading: false, error: null });
    void this.fetchRefillRequirements();
    // keepAlive: without this, the in-memory state survives a logout / login-as-different-user
    // in the same browser session and the home card renders the previous user's renewal. The
    // storage cache is already per-user, so only the in-memory state needs clearing here.
    // Clear on logout; clear + refetch when a different user signs in (mirrors NotificationsCubit).
    window.addEventListener(globalEvents.USER_CLEAR, this.handleUserClear);
    window.addEventListener(globalEvents.USER_CHANGED, this.handleUserChanged);
    // A requirement is fulfilled by submitting data elsewhere — a vital (DATA_SUBMITTED) or a
    // questionnaire incl. the refill survey (QUESTIONNAIRE_SAVED). Re-sync so the card reflects
    // the advanced renewal without waiting for the hub's open-refresh or a reload.
    document.addEventListener(
      globalEvents.DATA_SUBMITTED,
      this.handleRequirementDataChanged
    );
    document.addEventListener(
      globalEvents.QUESTIONNAIRE_SAVED,
      this.handleRequirementDataChanged
    );
  }

  private readonly handleUserClear = (): void => {
    this.clearData();
  };

  private readonly handleUserChanged = (): void => {
    // Drop the previous user's data immediately (covers the flag-off path and the in-flight
    // window), then load the new user's.
    this.clearData();
    void this.fetchRefillRequirements();
  };

  private readonly handleRequirementDataChanged = (): void => {
    this.revalidate();
  };

  private loadFromCache(): RefillRequirementsResponse | null {
    return StorageController.getItemParsed<RefillRequirementsResponse>(
      RefillRequirementsCubit.CACHE_KEY
    );
  }

  private saveToCache(data: RefillRequirementsResponse): void {
    StorageController.setItem(
      RefillRequirementsCubit.CACHE_KEY,
      JSON.stringify(data)
    );
  }

  private applyData(data: RefillRequirementsResponse): void {
    this.patch({ data, loading: false, error: null });
  }

  private readonly fetchFromApi = async (): Promise<void> => {
    try {
      const raw = await getRefillRequirements();
      const parsed = parseRefillRequirementsResponse(raw);
      this.saveToCache(parsed);
      this.applyData(parsed);
    } catch (error) {
      // A 404 is the contract signal for "no active renewal" — a normal empty state,
      // not an error. Clear silently so the card stays hidden; don't report to Sentry.
      if (error instanceof ApiError && error.status === 404) {
        this.clearData();
        return;
      }
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch refill requirements";
      this.patch({ loading: false, error: errorMessage });
      reportErrorSentry(error);
    }
  };

  public readonly fetchRefillRequirements = async (): Promise<void> => {
    if (!featureFlags.refillDecouplingEnabled) {
      return;
    }

    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.refillRequirements);

    this.patch({ loading: true, error: null });

    const cached = this.loadFromCache();
    if (cached) {
      this.applyData(cached);
      // Sync fresh data in the background
      void this.fetchFromApi();
    } else {
      await this.fetchFromApi();
    }

    loadingState.finish(LoadingKey.refillRequirements);
  };

  public readonly clearData = (): void => {
    StorageController.removeItem(RefillRequirementsCubit.CACHE_KEY);
    this.patch({ data: undefined, loading: false, error: null });
  };

  public readonly refresh = (): void => {
    void this.fetchRefillRequirements();
  };

  /**
   * Background re-sync from the API — no cache-apply and no loading toggle, so it can run
   * often (e.g. on every data submission) without flashing loading state. No-op when the flag
   * is off. A 404 clears the card, a fresh payload re-derives its variant (hidden once the
   * renewal has advanced).
   */
  public readonly revalidate = (): void => {
    if (!featureFlags.refillDecouplingEnabled) {
      return;
    }
    void this.fetchFromApi();
  };

  // ---- Derived selectors (logic lives here, not in JSX) ----

  public readonly getProgress = (): RefillRequirementsProgress =>
    getRequirementsProgress(this.state.data);

  public readonly getDaysUntilNextRefill = (now?: Date): number =>
    getDaysUntilNextRefill(this.state.data?.dueAt, now);

  public readonly getCountdownFraction = (now?: Date): number =>
    getCountdownFraction(this.getDaysUntilNextRefill(now));

  public readonly getCardVariant = (
    cohort: RefillCohort,
    now?: Date
  ): RefillCardVariant => getRefillCardVariant(this.state.data, cohort, now);
}
